/**
 * Reusable error boundary for the Helios web app.
 *
 * Why this exists
 * ───────────────
 * React error boundaries must be class components, so we centralize one
 * canonical implementation here and re-use it everywhere instead of letting
 * individual pages roll their own (or, worse, letting an entire page crash
 * to a white screen because nobody caught the throw).
 *
 * How to use it
 * ─────────────
 * - **Top-level (in `_app.tsx`)**: a `<AppErrorBoundary tone='page'>` is
 *   already wired around every route, so any unhandled render error in any
 *   page surfaces a friendly fallback instead of a blank document.
 * - **Page-level**: wrap a specific page (or a section under a feature
 *   flag) in another `<AppErrorBoundary tone='page'>` if you want a
 *   reset-able fallback that keeps the surrounding chrome (sidebar,
 *   layout) intact when the page body fails.
 * - **Island-level**: wrap a risky widget — a table fed by an external
 *   API, an editor preview, a chart — in
 *   `<AppErrorBoundary tone='inline'>`. The rest of the surface keeps
 *   working; the user gets a compact "this section had a problem" panel
 *   they can dismiss / retry.
 *
 * Telemetry
 * ─────────
 * Errors are forwarded to PostHog via `captureException` when the SDK is
 * configured, so unexpected crashes appear in the project's error tracking
 * dashboard alongside ingested stack traces.
 */
"use client";

import { AlertTriangle, RefreshCw } from "lucide-react";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { client as posthogClient, isConfigured as posthogConfigured } from "@/lib/posthog";
import { cn } from "@/lib/utils";

export type ErrorBoundaryTone = "page" | "inline";

interface AppErrorBoundaryProps {
  children: ReactNode;
  /**
   * `'page'` — full-bleed fallback intended to fill the document body /
   * route slot.
   * `'inline'` — compact card-shaped fallback meant for a single section
   * inside an otherwise healthy page. Defaults to `'inline'` because the
   * narrower variant is the safer choice when nesting.
   */
  tone?: ErrorBoundaryTone;
  /**
   * Optional human-readable scope label rendered in the fallback heading
   * (e.g. `"Matching customers"`, `"Segment editor"`). Helps users
   * pinpoint which section blew up when several boundaries are nested.
   */
  scopeLabel?: string;
  /**
   * Stable identity used to reset the boundary. When this value changes,
   * the boundary clears its caught error and re-renders `children`. Pair
   * with route params or form state to give the user a one-click retry.
   */
  resetKey?: unknown;
  /**
   * Optional custom fallback. Receives the caught error and a `reset`
   * thunk. When provided, takes full responsibility for the UI; the
   * built-in `tone`-based panels are skipped.
   */
  fallback?: (props: { error: Error; reset: () => void }) => ReactNode;
  /**
   * Side-effect hook so callers can wire additional telemetry (Sentry,
   * Datadog…) without touching this file. PostHog reporting always
   * happens regardless.
   */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface AppErrorBoundaryState {
  error: Error | null;
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Always log to the console so local debugging stays cheap.
    console.error("[AppErrorBoundary] caught render error:", error, info.componentStack);

    // Forward to PostHog when configured. Wrapped in try/catch so a broken
    // telemetry path can never replace the original error in the UI.
    if (posthogConfigured) {
      try {
        posthogClient.captureException(error, {
          scope: this.props.scopeLabel ?? "unknown",
          componentStack: info.componentStack,
        });
      } catch (telemetryErr) {
        console.error("[AppErrorBoundary] failed to forward to PostHog:", telemetryErr);
      }
    }

    this.props.onError?.(error, info);
  }

  componentDidUpdate(prevProps: AppErrorBoundaryProps) {
    // Allow the parent to reset the boundary by changing `resetKey` (e.g.
    // route id, segment id, retry counter). Without this hook the user
    // would have to hard-refresh to recover.
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) {
      return this.props.fallback({ error, reset: this.reset });
    }

    return (
      <AppErrorBoundaryFallback
        tone={this.props.tone ?? "inline"}
        scopeLabel={this.props.scopeLabel}
        error={error}
        onReset={this.reset}
      />
    );
  }
}

interface FallbackProps {
  tone: ErrorBoundaryTone;
  scopeLabel?: string;
  error: Error;
  onReset: () => void;
}

function AppErrorBoundaryFallback({ tone, scopeLabel, error, onReset }: FallbackProps) {
  const isPage = tone === "page";
  const headline = isPage
    ? "Something went wrong"
    : scopeLabel
      ? `${scopeLabel} had a problem`
      : "This section had a problem";

  return (
    <div
      role="alert"
      aria-live="assertive"
      className={cn(
        // Inline panels live inside other layouts → keep them compact and
        // self-contained. The page tone fills its container so the layout
        // chrome (sidebar, header) can stay visible around it.
        isPage
          ? "flex min-h-[60vh] w-full flex-col items-center justify-center gap-3 px-6 py-12 text-center"
          : "flex flex-col items-start gap-3 rounded-md border border-border bg-muted/30 p-4 text-left",
      )}
    >
      <div
        className={cn(
          "flex items-center gap-2 text-foreground",
          isPage ? "text-base font-semibold" : "text-sm font-medium",
        )}
      >
        <AlertTriangle
          aria-hidden="true"
          className={cn(isPage ? "size-5 text-destructive" : "size-4 text-destructive")}
        />
        {headline}
      </div>
      <p
        className={cn(
          "text-muted-foreground",
          isPage ? "max-w-md text-sm" : "text-sm leading-relaxed",
        )}
      >
        {isPage
          ? "An unexpected error broke this page. The team has been notified — you can try reloading the section or head back home."
          : "An unexpected error broke this section. The rest of the page is still usable. Try again, or refresh the page if it keeps happening."}
      </p>
      {process.env.NODE_ENV !== "production" ? (
        <pre className="max-w-full overflow-auto rounded border border-border bg-background/60 px-2 py-1 text-left font-mono text-xs text-muted-foreground">
          {error.message}
        </pre>
      ) : null}
      <div className="flex items-center gap-1.5">
        <Button
          type="button"
          size="sm"
          variant="outline"
          // Inline panels live inside a still-healthy page, so the soft
          // boundary reset is the right recovery — preserves surrounding
          // state. Page-tone errors mean the whole route crashed, so a
          // hard reload is the only reliable retry.
          onClick={() => {
            if (isPage) {
              if (typeof window !== "undefined") window.location.reload();
              return;
            }
            onReset();
          }}
        >
          <RefreshCw aria-hidden="true" className="mr-1 size-3.5" />
          Try again
        </Button>
        {isPage ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              if (typeof window !== "undefined") window.location.href = "/messages";
            }}
          >
            Back to home
          </Button>
        ) : null}
      </div>
    </div>
  );
}
